import {
  amplitude,
  AmplitudeLogLevel,
  AppAmplitudeLogger,
} from '@sennder/shell-utilities'

import { AMPLITUDE_API_KEY, MASK_SENSITIVE_DATA, TENANT } from '@/common/config'
import { getStateData, getUserProfile } from '@/store'
import { executeSafelyWithLogging } from './errors-handler'
import { logger } from './logger/loggers'

export function initAnalytics() {
  return executeSafelyWithLogging(() => {
    if (!AMPLITUDE_API_KEY) {
      throw new Error('[tms-shell - Analytics]: AMPLITUDE_API_KEY not found')
    }
    amplitude.init({
      token: AMPLITUDE_API_KEY,
      appName: 'tms',
      amplitudeConfig: {
        autocapture: {
          elementInteractions: true,
        },
        minIdLength: 3,
        loggerProvider: new AppAmplitudeLogger(logger),
        logLevel: AmplitudeLogLevel.Warn,
      },
    })
  }, 'amplitude.init')
}

export function identifyUserInAnalytics(onLogin: boolean) {
  const data = getStateData()
  const analyticsFeaturesFlags = Object.fromEntries(
    Object.entries(data.featureFlags).filter(([flag]) => flag.startsWith('exp'))
  )

  const profile = getUserProfile()

  const analyticsUser = {
    tenant: TENANT,
    userId: profile.id,
    userName: profile.email,
    language: profile.language,
    resolution: `${window.screen.width}x${window.screen.height}`,
    groupNames: data.roles,
    country: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...analyticsFeaturesFlags,
  }

  const deprecatedUserProperties: string[] = [
    'cookies',
    'featureFlags',
    'firstLogin',
    'firstName',
    'fullNameWithoutTitle',
    'id',
    'inCustomerGroup',
    'inFinanceGroup',
    'inStaffGroup',
    'initial_referrer',
    'initial_referring_domain',
    'initial_utm_medium',
    'initial_utm_source',
    'lastName',
    'paymentTermPopUpDaysPast',
    'permissions',
    'positionTitle',
    'selected',
    'shipperId',
    'tms id',
    'referrer',
    'referring_domain',
    'uiLanguage',
    'username',
    'uuid',
  ]

  executeSafelyWithLogging(
    () =>
      amplitude.identifyUser(
        analyticsUser,
        onLogin,
        undefined,
        deprecatedUserProperties,
        MASK_SENSITIVE_DATA,
        ['tenant', 'language', 'resolution']
      ),
    'amplitude.identifyUser'
  )
}

export const resetAnalyticsSession = () => {
  executeSafelyWithLogging(() => amplitude.endSession(), 'amplitude.endSession')
  initAnalytics()
}

// Used when the user rejects analytics cookies,
// all user data will be removed from the analytics integration as well as any event submissions will be terminated
export const stopAnalytics = () => {
  executeSafelyWithLogging(
    () => amplitude.stopTracking(),
    'amplitude.stopTracking'
  )
}

export function initSessionReplay() {
  executeSafelyWithLogging(
    () =>
      amplitude.initSessionReplay({
        sampleRate: 0.8,
      }),
    'amplitude.initSessionReplay'
  )
}

export function stopSessionReplay() {
  executeSafelyWithLogging(
    () => amplitude.stopSessionReplay(),
    'amplitude.stopSessionReplay'
  )
}
